/**
 * ─────────────────────────────────────────────────────────────────────────────
 * CENTRAL TRANSLATION SOURCE OF TRUTH
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Structure per key:
 *   text      {string}   Required. The translated string.
 *   className {string}   Optional. Extra Tailwind classes applied to that element
 *                        in this language (e.g. tighter tracking, smaller size).
 *   style     {object}   Optional. Inline styles (e.g. { minWidth: '140px' }).
 *
 * Usage in components:
 *   const { t } = useLanguage()
 *   const entry = t('some.key')           // { text, className?, style? }
 *   <h1 className={cn('base', entry.className)} style={entry.style}>
 *     {entry.text}
 *   </h1>
 *
 *   // Text-only shorthand:
 *   <span>{t('some.key').text}</span>
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const translations = {

  // ══════════════════════════════════════════════════════════════════════════
  // ENGLISH
  // ══════════════════════════════════════════════════════════════════════════
  en: {

    // ── Nav ─────────────────────────────────────────────────────────────────
    'nav.brand':     { text: 'Orion Fund' },
    'nav.landing':   { text: 'Landing' },
    'nav.dashboard': { text: 'Dashboard' },
    'nav.contact':   { text: 'Contact' },

    // ── DemoBanner ──────────────────────────────────────────────────────────
    'demo.label':       { text: 'Demo Mockup:' },
    'demo.description': { text: 'This is a demonstration dashboard with sample data. Real fund data will be implemented after governance approval.' },

    // ── Landing hero ─────────────────────────────────────────────────────────
    'landing.title':       { text: 'Orion Fund Dashboard' },
    'landing.description': { text: 'A live view of Cardano ecosystem health and on-chain impact since the Orion Fund\'s inception. The dashboard surfaces metrics such as TVL, on-chain activity, network revenue, and builder growth. New indicators are intended to be added as the initiatives mature, with community oversight built in at every stage. We welcome any and all community feedback.' },
    'landing.cta':         { text: 'View Dashboard' },
    'landing.data_note':   { text: 'Updated with mocked governance data' },

    // ── Landing KPIs ─────────────────────────────────────────────────────────
    'kpi.treasury_withdrawal.title': { text: 'Treasury Withdrawal Approved (Tranche 1)' },
    'kpi.treasury_withdrawal.desc':  { text: 'Approved in current governance action' },
    'kpi.converted_capital.title':   { text: 'Converted Capital Available' },
    'kpi.converted_capital.desc':    { text: 'USD/stablecoin equivalent at $0.30 ADA' },
    'kpi.capital_deployed.title':    { text: 'Capital Deployed' },
    'kpi.capital_deployed.desc':     { text: 'Actively invested to date' },
    'kpi.portfolio_companies.title': { text: 'Portfolio Companies' },
    'kpi.portfolio_companies.desc':  { text: 'Direct investments' },
    'kpi.ecosystem_initiatives.title': { text: 'Active Ecosystem Initiatives' },
    'kpi.ecosystem_initiatives.desc':  { text: 'Accelerator, venture studio, and ecosystem support' },
    'kpi.integrations.title': { text: 'Integrations' },
    'kpi.integrations.desc':  { text: 'Technical integrations completed' },

    // ── GovernanceApproval ───────────────────────────────────────────────────
    'gov.title':           { text: 'Current Governance Approval' },
    'gov.description':     { text: 'Approved tranche and governance constraints' },
    'gov.tranche_status':  { text: 'Tranche Status' },
    'gov.tranche_value':   { text: 'Tranche 1 Only' },
    'gov.treasury_withdrawal': { text: 'Treasury Withdrawal' },
    'gov.reference_value': { text: 'Reference Value' },
    'gov.reference_note':  { text: 'At $0.30 ADA proposal price' },
    'gov.future_tranches': { text: 'Future tranches require separate governance approval' },
    'gov.aggregate_cap':   { text: 'Aggregate cap across all tranches: 175M ADA' },

    // ── CapitalDeploymentAllocation ──────────────────────────────────────────
    'capital.title':              { text: 'Capital Deployment & Allocation' },
    'capital.description':        { text: 'Tranche 1 deployment progress and allocation breakdown' },
    'capital.overall_progress':   { text: 'Overall Deployment Progress' },
    'capital.deployed':           { text: 'Deployed:' },
    'capital.remaining':          { text: 'Remaining:' },
    'capital.by_category':        { text: 'Allocation by Category' },
    'capital.badge_operational':  { text: 'Operational' },
    'capital.badge_investment':   { text: 'Investment' },
    'capital.cat_direct':         { text: 'Direct Investments' },
    'capital.cat_growth':         { text: 'Growth Capital' },
    'capital.cat_acceleration':   { text: 'Startup Acceleration & Talent Pipeline' },
    'capital.cat_mgmt_fee':       { text: 'Management Fee' },
    'capital.cat_fund_expenses':  { text: 'Customary Fund Expenses' },
    'capital.cat_admin':          { text: 'Arouet Holdings Administration' },
    'capital.total_target':       { text: 'Total Target (Tranche 1)' },

    // ── PortfolioOverview ────────────────────────────────────────────────────
    'portfolio_overview.title':            { text: 'Portfolio Overview' },
    'portfolio_overview.stage_breakdown':  { text: 'Stage Breakdown' },
    'portfolio_overview.sector_breakdown': { text: 'Sector Breakdown' },
    'portfolio_overview.stage_preseed':    { text: 'Pre-seed' },
    'portfolio_overview.stage_seed':       { text: 'Seed' },
    'portfolio_overview.stage_late':       { text: 'Late' },
    'portfolio_overview.sector_defi':      { text: 'DeFi' },
    'portfolio_overview.sector_rwa':       { text: 'RWA' },
    'portfolio_overview.sector_infra':     { text: 'Infrastructure' },
    'portfolio_overview.sector_consumer':  { text: 'Consumer' },
    'portfolio_overview.companies':        { text: 'companies' },

    // ── PortfolioSnapshot ────────────────────────────────────────────────────
    'snapshot.title':            { text: 'Portfolio Snapshot' },
    'snapshot.description':      { text: 'Selected portfolio companies for transparency. Detailed terms, valuations, and performance data are not publicly disclosed.' },
    'snapshot.col_company':      { text: 'Company' },
    'snapshot.col_sector':       { text: 'Sector' },
    'snapshot.col_stage':        { text: 'Stage' },
    'snapshot.col_type':         { text: 'Type' },
    'snapshot.col_alignment':    { text: 'Alignment' },
    'snapshot.align_native':     { text: 'Native' },
    'snapshot.align_integrated': { text: 'Integrated' },
    'snapshot.note_label':       { text: 'Note:' },
    'snapshot.note':             { text: 'Full portfolio disclosed in quarterly reports (subject to confidentiality constraints).' },
    'snapshot.disclaimer_label': { text: 'Disclaimer:' },
    'snapshot.disclaimer':       { text: 'Portfolio information is provided for transparency purposes only. Inclusion does not constitute endorsement or investment solicitation. Certain information has been omitted to preserve confidentiality and comply with applicable regulations.' },

    // ── EcosystemImpact ──────────────────────────────────────────────────────
    'ecosystem.title':              { text: 'Ecosystem Metrics' },
    'ecosystem.description':        { text: 'Observable outcomes and ecosystem contributions' },
    'ecosystem.devs_onboarded':     { text: 'Developers Onboarded' },
    'ecosystem.accelerator':        { text: 'Accelerator Participants' },
    'ecosystem.native_projects':    { text: 'Cardano-Native Projects Supported' },
    'ecosystem.integrations':       { text: 'Integrations Completed' },
    'ecosystem.open_source':        { text: 'Open-Source Tooling Contributions' },
    'ecosystem.public_goods':       { text: 'Public Goods Contributions' },

    // ── InvestmentProgramActivity ────────────────────────────────────────────
    'activity.title':               { text: 'Investment & Program Activity' },
    'activity.description':         { text: 'Deal flow and program engagement actions' },
    'activity.deals_completed':     { text: 'Deals Completed' },
    'activity.deals_evaluated':     { text: 'Deals Evaluated' },
    'activity.studio_engagements':  { text: 'Venture Studio Engagements' },
    'activity.accelerator_cohorts': { text: 'Accelerator Cohorts' },
    'activity.period_quarter':      { text: 'This Quarter' },
    'activity.period_active':       { text: 'Active' },

    // ── OperationalExpenses ──────────────────────────────────────────────────
    'opex.title':              { text: 'Operational Expense Utilization' },
    'opex.description':        { text: 'Actual utilization of direct fund operational expenses (separate from ecosystem support)' },
    'opex.cat_mgmt_fee':       { text: 'Management Fee' },
    'opex.cat_fund_expenses':  { text: 'Customary Fund Expenses' },
    'opex.cat_admin':          { text: 'Arouet Holdings Administration' },
    'opex.target':             { text: 'Target:' },
    'opex.total':              { text: 'Total Operational Expenses' },
    'opex.utilized':           { text: '% utilized' },

    // ── TreasuryAlignmentRepayment ───────────────────────────────────────────
    'treasury.title':               { text: 'Treasury Alignment & Repayment' },
    'treasury.description':         { text: 'Capital accountability and waterfall structure per Section 4.4' },
    'treasury.ecosystem_support':   { text: 'Ecosystem Support & Investments' },
    'treasury.repayment_note':      { text: 'Subject to Treasury-first repayment' },
    'treasury.operational':         { text: 'Operational Expenses' },
    'treasury.opex_note':           { text: 'Separate from repayment waterfall' },
    'treasury.returned':            { text: 'Returned to Treasury' },
    'treasury.to_date':             { text: 'To date' },
    'treasury.waterfall_progress':  { text: 'Repayment Waterfall Progress' },
    'treasury.waterfall_label':     { text: 'Amount to be repaid before GP profit participation' },
    'treasury.repayment_status':    { text: 'Repayment begins upon portfolio exits' },
    'treasury.percent_repaid':      { text: '% repaid' },
    'treasury.capital_flow':        { text: 'Tranche 1 Capital Flow' },
    'treasury.flow_treasury':       { text: 'Treasury' },
    'treasury.flow_fund':           { text: 'Fund' },
    'treasury.flow_ecosystem':      { text: 'Ecosystem' },
    'treasury.flow_returns':        { text: 'Returns' },
    'treasury.waterfall_structure': { text: 'Waterfall Structure' },
    'treasury.rule_1': { text: '100% of Ecosystem Support & Investments capital must be returned to Treasury' },
    'treasury.rule_2': { text: 'Only after full repayment does GP profit participation begin' },
    'treasury.rule_3': { text: 'Operational expenses are separate and not subject to this waterfall' },

    // ── CommunityOversight ───────────────────────────────────────────────────
    'oversight.title':              { text: 'Community Oversight & Governance' },
    'oversight.description':        { text: 'Transparency mechanisms and community engagement' },
    'oversight.quarterly_reports':  { text: 'Quarterly Reports' },
    'oversight.last_report':        { text: 'Last Report' },
    'oversight.next_report':        { text: 'Next Report' },
    'oversight.view_archive':       { text: 'View Archive →' },
    'oversight.amas':               { text: 'AMAs & Roundtables' },
    'oversight.last_ama':           { text: 'Last AMA' },
    'oversight.next_session':       { text: 'Next Session' },
    'oversight.join_session':       { text: 'Join Next Session →' },
    'oversight.community_director': { text: 'Community Director' },
    'oversight.status':             { text: 'Status' },
    'oversight.active':             { text: 'Active' },
    'oversight.appointed':          { text: 'Appointed' },
    'oversight.gov_updates':        { text: 'Governance Updates' },
    'oversight.published_quarter':  { text: 'Published This Quarter' },
    'oversight.view_updates':       { text: 'View Updates →' },
    'oversight.side_letter':        { text: 'Side Letter' },
    'oversight.disclosure_status':  { text: 'Public Disclosure Status' },
    'oversight.published':          { text: 'Published' },
    'oversight.view_document':      { text: 'View Document →' },
    'oversight.roundtables':        { text: 'Ecosystem Roundtables' },
    'oversight.frequency':          { text: 'Frequency' },
    'oversight.quarterly':          { text: 'Quarterly' },
    'oversight.next_roundtable':    { text: 'Next Roundtable' },

    // ── DisclosureNote ───────────────────────────────────────────────────────
    'disclosure.title':              { text: 'Disclosure Note' },
    'disclosure.scope_label':        { text: 'Scope of Public Reporting:' },
    'disclosure.scope':              { text: 'This dashboard reports ecosystem-level, operational, and publicly disclosable fund information. Confidential portfolio terms, valuations, and transaction details may be withheld where required by law, regulation, or fund confidentiality obligations.' },
    'disclosure.not_offer_label':    { text: 'Not an Offer or Solicitation:' },
    'disclosure.not_offer':          { text: 'This dashboard is for informational and transparency purposes only. It does not constitute an offer to sell or a solicitation to buy any securities or investment products.' },
    'disclosure.no_guarantee_label': { text: 'No Performance Guarantees:' },
    'disclosure.no_guarantee':       { text: 'Past deployment activity and ecosystem metrics do not guarantee future results. All investments involve risk, including possible loss of principal.' },
    'disclosure.data_delay_label':   { text: 'Data Freshness:' },
    'disclosure.data_delay':         { text: 'Dashboard data may not reflect real-time conditions. On-chain metrics, portfolio updates, and third-party data feeds may experience delays ranging from minutes to days. Users should not rely on this dashboard for time-sensitive trading or any investment decisions.' },
    'disclosure.currency_label':     { text: 'ADA/USD Conversions:' },
    'disclosure.currency':           { text: 'All ADA to USD conversions use the current ADA price fetched live from the CoinGecko API. Historical values are converted using today\'s exchange rate, not the rate at the time of the original transaction or data point.' },
    'disclosure.methodology_label':  { text: 'Methodology:' },
    'disclosure.methodology':        { text: 'Metrics are calculated based on observable on-chain data, portfolio company reporting, and fund operational records. Ecosystem data is sourced from third-party platforms including Blockfrost (Cardano blockchain data) and Dune Analytics (on-chain analytics). Detailed methodology documentation is available upon request.' },
    'disclosure.view_full':          { text: 'View Full Disclosure & Methodology Documentation' },

    // ── Dashboard section headings ───────────────────────────────────────────
    'dash.financial_metrics':      { text: 'Key Financial Metrics' },
    'dash.deployment_progress':    { text: 'Fund Deployment Progress' },
    'dash.deployed_capital':       { text: 'Deployed Capital' },
    'dash.deployment_since':       { text: 'since start of fund' },
    'dash.deployment_over':        { text: 'over the past' },
    'dash.charts':                 { text: 'Charts' },
    'dash.ecosystem_health':       { text: 'Ecosystem Health' },
    'dash.usage_adoption':         { text: 'Usage & Adoption' },
    'dash.developers_network':     { text: 'Developers & Network' },
    'dash.community_metrics':      { text: 'Community Metrics' },
    'dash.section_onchain':        { text: 'Orion’s Focus: Advancing Cardano’s 2030 Goals' },
    'dash.section_orion_activity': { text: 'Orion Activity & Growth Accountability' },
    'dash.section_fund_deploy':    { text: 'Orion Fund Deployment' },
    'dash.disclaimer':             { text: 'This dashboard is designed to provide public visibility into ecosystem-level progress, capital deployment, and on-chain metrics. Detailed, auditable fund-level financial performance is reported separately to the SPV Directors (including the Community Director) as part of formal oversight and governance processes, in accordance with investor obligations and regulatory requirements.' },

    // ── Dashboard metric card labels ─────────────────────────────────────────
    'metric.capital_deployment':          { text: 'Capital Deployment' },
    'metric.portfolio_companies':         { text: 'Portfolio Companies' },
    'metric.tvl':                         { text: 'Total Value Locked' },
    'metric.active_developers':           { text: 'Active Developers' },
    'metric.on_chain_addresses':          { text: 'Total On-Chain Addresses' },
    'metric.monthly_active_addresses':    { text: 'Monthly Active Addresses' },
    'metric.new_smart_contracts':         { text: 'New Smart Contracts' },
    'metric.on_chain_integrations':       { text: 'On-Chain Integrations' },
    'metric.monthly_active_devs':         { text: 'Monthly Active Developers' },
    'metric.devs_onboarded':              { text: 'Developers Onboarded' },
    'metric.stablecoin_volume':           { text: 'Stablecoin Volume' },
    'metric.portfolio_mainnet':           { text: 'Portfolio Live on Mainnet' },
    'metric.governance_participation':    { text: 'Governance Participation' },
    'metric.community_size':              { text: 'Community Size' },
    'metric.ecosystem_integrations':      { text: 'Ecosystem Integrations Sourced & Completed' },
    'metric.companies_incubated':         { text: 'Companies Incubated\nvia Venture Studio' },
    'metric.companies_accelerated':       { text: 'Companies Accelerated (Growth Accelerator)' },
    'metric.founders_hacker_house':       { text: 'Founders Developed via Hacker House' },
    'metric.orion_portfolio_companies':   { text: 'Number of Portfolio Companies' },

    // ── TVLChart ─────────────────────────────────────────────────────────────
    'chart.tvl.title':          { text: 'Total Value Locked (TVL)' },
    'chart.tvl.subtitle':       { text: 'Assets locked in DeFi & RWA protocols' },
    'chart.tvl.growth_tooltip': { text: 'Growth rate for selected timeframe' },
    'chart.tvl.tooltip_label':  { text: 'TVL: ' },

    // ── MAUChart ─────────────────────────────────────────────────────────────
    'chart.mau.title':          { text: 'Monthly Active Wallets (MAU)' },
    'chart.mau.title_monthly':  { text: 'Monthly Active Wallets (MAU)' },
    'chart.mau.subtitle':       { text: 'Unique wallets transacting on-chain monthly' },
    'chart.mau.growth_tooltip': { text: 'Wallet growth over selected period' },
    'chart.mau.tooltip_label':  { text: 'Wallets: ' },

    // ── VolumeChart ──────────────────────────────────────────────────────────
    'chart.volume.title':         { text: 'On-Chain Transactions' },
    'chart.volume.title_monthly': { text: 'Monthly On-Chain Transactions' },
    'chart.volume.subtitle':      { text: 'Network demand & real-world utility signal' },
    'chart.volume.yoy_growth':    { text: 'YoY Growth' },
    'chart.volume.avg_monthly':   { text: 'Avg Monthly' },
    'chart.volume.yoy_tooltip':   { text: 'Year-over-year growth rate' },
    'chart.volume.avg_tooltip':   { text: 'Average monthly transaction volume' },
    'chart.volume.tooltip_label': { text: 'Volume: ' },

    // ── MADChart ─────────────────────────────────────────────────────────────
    'chart.mad.title':          { text: 'Monthly Active Developers' },
    'chart.mad.title_monthly':  { text: 'Monthly Active Developers' },
    'chart.mad.subtitle':       { text: 'Active builders committing to Cardano monthly' },
    'chart.mad.growth_tooltip': { text: 'Developer growth over selected period' },
    'chart.mad.tooltip_label':  { text: 'Developers: ' },

    // ── ProtocolRevenueChart ─────────────────────────────────────────────────
    'chart.revenue.title':          { text: 'Annual Protocol Revenue (ADA)' },
    'chart.revenue.title_monthly':  { text: 'Monthly Protocol Revenue (ADA)' },
    'chart.revenue.subtitle':       { text: 'ADA fees driving network self-sufficiency' },
    'chart.revenue.growth_tooltip': { text: 'Revenue growth over selected period' },
    'chart.revenue.tooltip_label':  { text: 'Revenue: ' },

    // ── PortfolioChart ───────────────────────────────────────────────────────
    'chart.portfolio.title':              { text: 'Portfolio Distribution' },
    'chart.portfolio.all_companies':      { text: 'All companies by sector' },
    'chart.portfolio.new_companies_pre':  { text: 'New companies this ' },
    'chart.portfolio.new_companies_post': { text: '' },
    'chart.portfolio.companies':          { text: 'companies' },
    'chart.portfolio.no_companies':       { text: 'No new companies in this timeframe' },
    'chart.portfolio.try_longer':         { text: 'Try selecting a longer timeframe' },

    // ── CompaniesTable ───────────────────────────────────────────────────────
    'table.heading':          { text: 'Portfolio Companies' },
    'table.all_sectors':      { text: 'All Sectors' },
    'table.col_company':      { text: 'Company' },
    'table.col_sector':       { text: 'Sector' },
    'table.col_stage':        { text: 'Stage' },
    'table.col_date':         { text: 'Investment Date' },
    'table.col_mainnet':      { text: 'Mainnet' },
    'table.col_raised':       { text: 'Total Raised' },
    'table.col_coinvestors':  { text: 'Co-Investors' },
    'table.no_companies':     { text: 'No companies found' },
    'table.no_match':         { text: 'No companies match the selected filters.' },
    'table.no_timeframe':     { text: 'No companies added in the selected timeframe.' },
    'table.try_all_sectors':  { text: "Try selecting 'All Sectors' or a longer timeframe." },
    'table.showing':          { text: 'Showing' },
    'table.companies_count':  { text: 'companies' },
    'table.live_mainnet':     { text: 'Live on Mainnet' },
    'table.live':             { text: 'Live' },
    'table.pre_launch':       { text: 'Pre-launch' },
    'table.col_alignment':    { text: 'Alignment' },
    'table.align_native':     { text: 'Native' },
    'table.align_integrated': { text: 'Integrated' },

    // ── Sector display names ─────────────────────────────────────────────────
    'sector.defi_infra':  { text: 'DeFi Infrastructure' },
    'sector.rwa':         { text: 'RWA & Tokenization' },
    'sector.enterprise':  { text: 'Enterprise & Scaling' },
    'sector.emerging':    { text: 'Emerging Opportunities' },

    // ── Company Detail Page ──────────────────────────────────────────────────
    'company.back_to_dashboard': { text: 'Back to Dashboard' },
    'company.not_found': { text: 'Company Not Found' },
    'company.not_found_desc': { text: "The company you're looking for doesn't exist." },
    'company.live_on_mainnet': { text: 'Live on Mainnet' },
    'company.pre_launch': { text: 'Pre-launch' },
    'company.valuation': { text: 'Valuation' },
    'company.total_raised': { text: 'Total Raised' },
    'company.funding_date': { text: 'Funding Date' },
    'company.co_investors': { text: 'Co-Investors' },
    'company.demo_feature': { text: 'Demo Feature' },
    'company.demo_desc': { text: 'This detailed company page is available for demonstration purposes only. In the full version, all portfolio companies will have dedicated pages with comprehensive information.' },
  },


  // ══════════════════════════════════════════════════════════════════════════
  // JAPANESE
  // ══════════════════════════════════════════════════════════════════════════
  ja: {

    // ── Nav ─────────────────────────────────────────────────────────────────
    'nav.brand':     { text: 'オリオンファンド' },
    'nav.landing':   { text: 'ホーム' },
    'nav.dashboard': { text: 'ダッシュボード', style: { minWidth: '110px' } },
    'nav.contact':   { text: 'お問い合わせ', style: { minWidth: '100px' } },

    // ── DemoBanner ──────────────────────────────────────────────────────────
    'demo.label':       { text: 'デモ版:' },
    'demo.description': { text: 'サンプルデータを使用したデモダッシュボードです。実際のファンドデータはガバナンス承認後に実装されます。' },

    // ── Landing hero ─────────────────────────────────────────────────────────
    'landing.title':       { text: 'オリオンファンド ダッシュボード', className: 'text-2xl sm:text-3xl lg:text-4xl' },
    'landing.description': { text: 'オリオンファンド設立以来のCardanoエコシステムの健全性とオンチェーンインパクトをリアルタイムで表示します。TVL、オンチェーン活動、ネットワーク収益、開発者の成長などの指標を提供しています。イニシアチブの成熟に伴い新しい指標を追加する予定であり、すべての段階でコミュニティの監視が組み込まれています。コミュニティからのフィードバックを歓迎します。' },
    'landing.cta':         { text: 'ダッシュボードを見る', style: { minWidth: '160px' } },
    'landing.data_note':   { text: '模擬ガバナンスデータで更新済み' },

    // ── Landing KPIs ─────────────────────────────────────────────────────────
    'kpi.treasury_withdrawal.title': { text: '財務省引き出し承認（トランシェ1）' },
    'kpi.treasury_withdrawal.desc':  { text: '現在のガバナンスアクションで承認済み' },
    'kpi.converted_capital.title':   { text: '換算資本利用可能額' },
    'kpi.converted_capital.desc':    { text: 'ADA $0.30換算のUSD/ステーブルコイン相当額' },
    'kpi.capital_deployed.title':    { text: '展開済み資本' },
    'kpi.capital_deployed.desc':     { text: '現在までの投資額' },
    'kpi.portfolio_companies.title': { text: 'ポートフォリオ企業数' },
    'kpi.portfolio_companies.desc':  { text: '直接投資企業' },
    'kpi.ecosystem_initiatives.title': { text: 'アクティブエコシステム施策数' },
    'kpi.ecosystem_initiatives.desc':  { text: 'アクセラレーター、ベンチャースタジオ、エコシステム支援' },
    'kpi.integrations.title': { text: 'インテグレーション数' },
    'kpi.integrations.desc':  { text: '完了した技術統合数' },

    // ── GovernanceApproval ───────────────────────────────────────────────────
    'gov.title':           { text: '現在のガバナンス承認' },
    'gov.description':     { text: '承認済みトランシェとガバナンス制約' },
    'gov.tranche_status':  { text: 'トランシェステータス' },
    'gov.tranche_value':   { text: 'トランシェ1のみ' },
    'gov.treasury_withdrawal': { text: '財務省引き出し' },
    'gov.reference_value': { text: '参考値' },
    'gov.reference_note':  { text: 'ADA提案価格$0.30時点' },
    'gov.future_tranches': { text: '将来のトランシェには別途ガバナンス承認が必要です' },
    'gov.aggregate_cap':   { text: '全トランシェ合計上限：1億7500万ADA' },

    // ── CapitalDeploymentAllocation ──────────────────────────────────────────
    'capital.title':              { text: '資本展開・配分' },
    'capital.description':        { text: 'トランシェ1展開進捗と配分内訳' },
    'capital.overall_progress':   { text: '全体展開進捗' },
    'capital.deployed':           { text: '展開済み：' },
    'capital.remaining':          { text: '残余：' },
    'capital.by_category':        { text: 'カテゴリ別配分' },
    'capital.badge_operational':  { text: 'オペレーション' },
    'capital.badge_investment':   { text: '投資' },
    'capital.cat_direct':         { text: '直接投資' },
    'capital.cat_growth':         { text: '成長資本' },
    'capital.cat_acceleration':   { text: 'スタートアップ加速・人材育成' },
    'capital.cat_mgmt_fee':       { text: '運用手数料' },
    'capital.cat_fund_expenses':  { text: '通常ファンド費用' },
    'capital.cat_admin':          { text: 'アルエ・ホールディングス管理費', className: 'text-xs' },
    'capital.total_target':       { text: '合計目標（トランシェ1）' },

    // ── PortfolioOverview ────────────────────────────────────────────────────
    'portfolio_overview.title':            { text: 'ポートフォリオ概要' },
    'portfolio_overview.stage_breakdown':  { text: 'ステージ別内訳' },
    'portfolio_overview.sector_breakdown': { text: 'セクター別内訳' },
    'portfolio_overview.stage_preseed':    { text: 'プレシード' },
    'portfolio_overview.stage_seed':       { text: 'シード' },
    'portfolio_overview.stage_late':       { text: 'レイター' },
    'portfolio_overview.sector_defi':      { text: 'DeFi' },
    'portfolio_overview.sector_rwa':       { text: 'RWA' },
    'portfolio_overview.sector_infra':     { text: 'インフラ' },
    'portfolio_overview.sector_consumer':  { text: 'コンシューマー' },
    'portfolio_overview.companies':        { text: '社' },

    // ── PortfolioSnapshot ────────────────────────────────────────────────────
    'snapshot.title':            { text: 'ポートフォリオスナップショット' },
    'snapshot.description':      { text: '透明性のための主要ポートフォリオ企業一覧。詳細な条件・評価額・パフォーマンスデータは非公開です。' },
    'snapshot.col_company':      { text: '企業' },
    'snapshot.col_sector':       { text: 'セクター' },
    'snapshot.col_stage':        { text: 'ステージ' },
    'snapshot.col_type':         { text: 'タイプ' },
    'snapshot.col_alignment':    { text: '整合性' },
    'snapshot.align_native':     { text: 'ネイティブ' },
    'snapshot.align_integrated': { text: '統合済み' },
    'snapshot.note_label':       { text: '注意：' },
    'snapshot.note':             { text: '完全なポートフォリオは四半期報告書で開示（機密性制約あり）。' },
    'snapshot.disclaimer_label': { text: '免責事項：' },
    'snapshot.disclaimer':       { text: 'ポートフォリオ情報は透明性目的のみで提供されます。掲載は推薦や投資勧誘を意味しません。一部情報は機密保持および法規制順守のため省略されています。' },

    // ── EcosystemImpact ──────────────────────────────────────────────────────
    'ecosystem.title':           { text: 'エコシステム指標' },
    'ecosystem.description':     { text: '観測可能な成果とエコシステムへの貢献' },
    'ecosystem.devs_onboarded':  { text: '開発者オンボード数' },
    'ecosystem.accelerator':     { text: 'アクセラレーター参加者数' },
    'ecosystem.native_projects': { text: 'Cardanoネイティブ支援プロジェクト数' },
    'ecosystem.integrations':    { text: '完了統合数' },
    'ecosystem.open_source':     { text: 'オープンソース貢献数' },
    'ecosystem.public_goods':    { text: '公共財貢献数' },

    // ── InvestmentProgramActivity ────────────────────────────────────────────
    'activity.title':               { text: '投資・プログラム活動' },
    'activity.description':         { text: 'ディールフローとプログラム参加状況' },
    'activity.deals_completed':     { text: '完了ディール数' },
    'activity.deals_evaluated':     { text: '評価ディール数' },
    'activity.studio_engagements':  { text: 'ベンチャースタジオ活動数' },
    'activity.accelerator_cohorts': { text: 'アクセラレーターコホート数' },
    'activity.period_quarter':      { text: '今四半期' },
    'activity.period_active':       { text: 'アクティブ' },

    // ── OperationalExpenses ──────────────────────────────────────────────────
    'opex.title':             { text: '運用費用利用状況' },
    'opex.description':       { text: '直接ファンド運用費用の実際の利用状況（エコシステム支援費を除く）' },
    'opex.cat_mgmt_fee':      { text: '運用手数料' },
    'opex.cat_fund_expenses': { text: '通常ファンド費用' },
    'opex.cat_admin':         { text: 'アルエ・ホールディングス管理費' },
    'opex.target':            { text: '目標：' },
    'opex.total':             { text: '運用費用合計' },
    'opex.utilized':          { text: '% 利用済み' },

    // ── TreasuryAlignmentRepayment ───────────────────────────────────────────
    'treasury.title':               { text: '財務整合性・返済' },
    'treasury.description':         { text: '第4.4条に基づく資本説明責任とウォーターフォール構造' },
    'treasury.ecosystem_support':   { text: 'エコシステム支援・投資' },
    'treasury.repayment_note':      { text: '財務省優先返済対象' },
    'treasury.operational':         { text: '運用費用' },
    'treasury.opex_note':           { text: '返済ウォーターフォール対象外' },
    'treasury.returned':            { text: '財務省への返済額' },
    'treasury.to_date':             { text: '現在まで' },
    'treasury.waterfall_progress':  { text: '返済ウォーターフォール進捗' },
    'treasury.waterfall_label':     { text: 'GP利益分配前に返済が必要な金額' },
    'treasury.repayment_status':    { text: 'ポートフォリオ出口時に返済開始' },
    'treasury.percent_repaid':      { text: '% 返済済み' },
    'treasury.capital_flow':        { text: 'トランシェ1資本フロー' },
    'treasury.flow_treasury':       { text: '財務省' },
    'treasury.flow_fund':           { text: 'ファンド' },
    'treasury.flow_ecosystem':      { text: 'エコシステム' },
    'treasury.flow_returns':        { text: 'リターン' },
    'treasury.waterfall_structure': { text: 'ウォーターフォール構造' },
    'treasury.rule_1': { text: 'エコシステム支援・投資資本の100%を財務省に返済する必要があります' },
    'treasury.rule_2': { text: '完全返済後にのみGP利益分配が開始されます' },
    'treasury.rule_3': { text: '運用費用は別扱いでこのウォーターフォールの対象外です' },

    // ── CommunityOversight ───────────────────────────────────────────────────
    'oversight.title':              { text: 'コミュニティ監視・ガバナンス' },
    'oversight.description':        { text: '透明性メカニズムとコミュニティ参加' },
    'oversight.quarterly_reports':  { text: '四半期報告書' },
    'oversight.last_report':        { text: '前回の報告' },
    'oversight.next_report':        { text: '次回の報告' },
    'oversight.view_archive':       { text: 'アーカイブを見る →' },
    'oversight.amas':               { text: 'AMA・ラウンドテーブル' },
    'oversight.last_ama':           { text: '前回のAMA' },
    'oversight.next_session':       { text: '次回セッション' },
    'oversight.join_session':       { text: '次のセッションに参加 →' },
    'oversight.community_director': { text: 'コミュニティディレクター' },
    'oversight.status':             { text: 'ステータス' },
    'oversight.active':             { text: 'アクティブ' },
    'oversight.appointed':          { text: '任命日' },
    'oversight.gov_updates':        { text: 'ガバナンス更新情報' },
    'oversight.published_quarter':  { text: '今四半期の公開情報' },
    'oversight.view_updates':       { text: '更新を見る →' },
    'oversight.side_letter':        { text: 'サイドレター' },
    'oversight.disclosure_status':  { text: '公開開示状況' },
    'oversight.published':          { text: '公開済み' },
    'oversight.view_document':      { text: '書類を見る →' },
    'oversight.roundtables':        { text: 'エコシステムラウンドテーブル' },
    'oversight.frequency':          { text: '開催頻度' },
    'oversight.quarterly':          { text: '四半期ごと' },
    'oversight.next_roundtable':    { text: '次回ラウンドテーブル' },

    // ── DisclosureNote ───────────────────────────────────────────────────────
    'disclosure.title':              { text: '開示に関する注記' },
    'disclosure.scope_label':        { text: '公開報告の範囲：' },
    'disclosure.scope':              { text: 'このダッシュボードはエコシステムレベル、運用上、公開可能なファンド情報を報告します。機密性のあるポートフォリオ条件、評価額、取引の詳細は法律・規制・ファンドの機密義務により非公開となる場合があります。' },
    'disclosure.not_offer_label':    { text: '勧誘ではありません：' },
    'disclosure.not_offer':          { text: 'このダッシュボードは情報提供と透明性のみを目的としています。有価証券や投資商品の販売または購入を勧誘するものではありません。' },
    'disclosure.no_guarantee_label': { text: 'パフォーマンス保証なし：' },
    'disclosure.no_guarantee':       { text: '過去の展開活動やエコシステム指標は将来の結果を保証するものではありません。すべての投資には元本損失のリスクが含まれます。' },
    'disclosure.data_delay_label':   { text: 'データの鮮度：' },
    'disclosure.data_delay':         { text: 'ダッシュボードのデータはリアルタイムの状況を反映していない場合があります。オンチェーン指標、ポートフォリオ更新、サードパーティデータフィードは数分から数日の遅延が発生する可能性があります。時間的制約のある取引や投資判断にこのダッシュボードを使用しないでください。' },
    'disclosure.currency_label':     { text: 'ADA/USD換算：' },
    'disclosure.currency':           { text: 'すべてのADAからUSDへの換算は、CoinGecko APIからリアルタイムで取得した現在のADA価格を使用します。過去の値は、元の取引またはデータポイント時点のレートではなく、本日の為替レートで換算されます。' },
    'disclosure.methodology_label':  { text: '方法論：' },
    'disclosure.methodology':        { text: '指標はオンチェーンデータ、ポートフォリオ企業の報告、ファンド運用記録に基づいて計算されます。エコシステムデータはBlockfrost（Cardanoブロックチェーンデータ）、Dune Analytics（オンチェーン分析）を含むサードパーティプラットフォームから取得されます。詳細な方法論はご要望に応じてご提供します。' },
    'disclosure.view_full':          { text: '完全な開示・方法論ドキュメントを見る' },

    // ── Dashboard section headings ───────────────────────────────────────────
    'dash.financial_metrics':      { text: '主要財務指標' },
    'dash.deployment_progress':    { text: 'ファンド展開進捗' },
    'dash.deployed_capital':       { text: '展開済み資本' },
    'dash.deployment_since':       { text: 'ファンド開始以来' },
    'dash.deployment_over':        { text: '過去' },
    'dash.charts':                 { text: 'チャート' },
    'dash.ecosystem_health':       { text: 'エコシステム健全性' },
    'dash.usage_adoption':         { text: '利用状況・採用' },
    'dash.developers_network':     { text: '開発者・ネットワーク' },
    'dash.community_metrics':      { text: 'コミュニティ指標' },
    'dash.section_onchain':        { text: 'Orionの注力領域：Cardano 2030年目標の推進' },
    'dash.section_orion_activity': { text: 'オリオン活動・成長説明責任' },
    'dash.section_fund_deploy':    { text: 'オリオンファンド展開状況' },
    'dash.disclaimer':             { text: 'このダッシュボードはエコシステムレベルの進捗、資本展開、オンチェーン指標の公開可視性を提供します。詳細な監査可能なファンドレベルの財務パフォーマンスは、投資家義務と規制要件に従い、正式な監視・ガバナンスプロセスの一環としてSPVディレクターに別途報告されます。' },

    // ── Dashboard metric card labels ─────────────────────────────────────────
    'metric.capital_deployment':        { text: '資本展開率' },
    'metric.portfolio_companies':       { text: 'ポートフォリオ企業数' },
    'metric.tvl':                       { text: '総預かり資産（TVL）' },
    'metric.active_developers':         { text: 'アクティブ開発者数' },
    'metric.on_chain_addresses':        { text: 'オンチェーンアドレス総数' },
    'metric.monthly_active_addresses':  { text: '月間アクティブアドレス数' },
    'metric.new_smart_contracts':       { text: '新規スマートコントラクト数' },
    'metric.on_chain_integrations':     { text: 'オンチェーン統合数' },
    'metric.monthly_active_devs':       { text: '月間アクティブ開発者数' },
    'metric.devs_onboarded':            { text: '開発者オンボード数' },
    'metric.stablecoin_volume':         { text: 'ステーブルコイン取引量' },
    'metric.portfolio_mainnet':         { text: 'メインネット稼働率' },
    'metric.governance_participation':  { text: 'ガバナンス参加率' },
    'metric.community_size':            { text: 'コミュニティ規模' },
    'metric.ecosystem_integrations':    { text: 'エコシステム統合（実施・完了）', className: 'text-xs' },
    'metric.companies_incubated':       { text: 'ベンチャースタジオ経由インキュベーション企業数', className: 'text-xs' },
    'metric.companies_accelerated':     { text: 'アクセラレーター経由支援企業数', className: 'text-xs' },
    'metric.founders_hacker_house':     { text: 'ハッカーハウス育成ファウンダー数', className: 'text-xs' },
    'metric.orion_portfolio_companies': { text: 'ポートフォリオ企業総数' },

    // ── TVLChart ─────────────────────────────────────────────────────────────
    'chart.tvl.title':          { text: '総預かり資産（TVL）' },
    'chart.tvl.subtitle':       { text: 'DeFi・RWAプロトコルのロック資産' },
    'chart.tvl.growth_tooltip': { text: '選択期間の成長率' },
    'chart.tvl.tooltip_label':  { text: 'TVL: ' },

    // ── MAUChart ─────────────────────────────────────────────────────────────
    'chart.mau.title':          { text: '月間アクティブウォレット（MAU）' },
    'chart.mau.title_monthly':  { text: '月間アクティブウォレット（MAU）' },
    'chart.mau.subtitle':       { text: '月間オンチェーン取引ウォレット数' },
    'chart.mau.growth_tooltip': { text: '選択期間のウォレット成長率' },
    'chart.mau.tooltip_label':  { text: 'ウォレット数: ' },

    // ── VolumeChart ──────────────────────────────────────────────────────────
    'chart.volume.title':         { text: 'オンチェーン取引数' },
    'chart.volume.title_monthly': { text: '月間オンチェーン取引数' },
    'chart.volume.subtitle':      { text: 'ネットワーク需要・実用性シグナル' },
    'chart.volume.yoy_growth':    { text: '前年比成長率' },
    'chart.volume.avg_monthly':   { text: '月間平均' },
    'chart.volume.yoy_tooltip':   { text: '前年比成長率' },
    'chart.volume.avg_tooltip':   { text: '月間平均取引量' },
    'chart.volume.tooltip_label': { text: '取引量: ' },

    // ── MADChart ─────────────────────────────────────────────────────────────
    'chart.mad.title':          { text: '月間アクティブ開発者数' },
    'chart.mad.title_monthly':  { text: '月間アクティブ開発者数' },
    'chart.mad.subtitle':       { text: '月間Cardanoコミット開発者数' },
    'chart.mad.growth_tooltip': { text: '選択期間の開発者成長率' },
    'chart.mad.tooltip_label':  { text: '開発者数: ' },

    // ── ProtocolRevenueChart ─────────────────────────────────────────────────
    'chart.revenue.title':          { text: '年間プロトコル収益（ADA）' },
    'chart.revenue.title_monthly':  { text: '月間プロトコル収益（ADA）' },
    'chart.revenue.subtitle':       { text: 'ネットワーク自立を支えるADA手数料' },
    'chart.revenue.growth_tooltip': { text: '選択期間の収益成長率' },
    'chart.revenue.tooltip_label':  { text: '収益: ' },

    // ── PortfolioChart ───────────────────────────────────────────────────────
    'chart.portfolio.title':              { text: 'ポートフォリオ分布' },
    'chart.portfolio.all_companies':      { text: 'セクター別全企業' },
    'chart.portfolio.new_companies_pre':  { text: '' },
    'chart.portfolio.new_companies_post': { text: 'の新規企業' },
    'chart.portfolio.companies':          { text: '社' },
    'chart.portfolio.no_companies':       { text: 'この期間に新規企業はありません' },
    'chart.portfolio.try_longer':         { text: 'より長い期間を選択してください' },

    // ── CompaniesTable ───────────────────────────────────────────────────────
    'table.heading':          { text: 'ポートフォリオ企業' },
    'table.all_sectors':      { text: '全セクター' },
    'table.col_company':      { text: '企業名' },
    'table.col_sector':       { text: 'セクター' },
    'table.col_stage':        { text: 'ステージ' },
    'table.col_date':         { text: '投資日' },
    'table.col_mainnet':      { text: 'メインネット' },
    'table.col_raised':       { text: '調達総額' },
    'table.col_coinvestors':  { text: '共同投資家' },
    'table.no_companies':     { text: '企業が見つかりません' },
    'table.no_match':         { text: '選択したフィルターに一致する企業はありません。' },
    'table.no_timeframe':     { text: '選択した期間に追加された企業はありません。' },
    'table.try_all_sectors':  { text: '「全セクター」またはより長い期間を選択してください。' },
    'table.showing':          { text: '表示中' },
    'table.companies_count':  { text: '社' },
    'table.live_mainnet':     { text: 'メインネット稼働中' },
    'table.live':             { text: '稼働中' },
    'table.pre_launch':       { text: '未稼働' },
    'table.col_alignment':    { text: '整合性' },
    'table.align_native':     { text: 'ネイティブ' },
    'table.align_integrated': { text: '統合済み' },

    // ── Sector display names ─────────────────────────────────────────────────
    'sector.defi_infra':  { text: 'DeFiインフラ' },
    'sector.rwa':         { text: 'RWA・トークン化' },
    'sector.enterprise':  { text: 'エンタープライズ・スケーリング' },
    'sector.emerging':    { text: '新興機会' },

    // ── Company Detail Page ──────────────────────────────────────────────────
    'company.back_to_dashboard': { text: 'ダッシュボードに戻る' },
    'company.not_found': { text: '企業が見つかりません' },
    'company.not_found_desc': { text: 'お探しの企業は存在しません。' },
    'company.live_on_mainnet': { text: 'メインネット稼働中' },
    'company.pre_launch': { text: '未稼働' },
    'company.valuation': { text: '評価額' },
    'company.total_raised': { text: '調達総額' },
    'company.funding_date': { text: '投資日' },
    'company.co_investors': { text: '共同投資家' },
    'company.demo_feature': { text: 'デモ機能' },
    'company.demo_desc': { text: 'この詳細な企業ページはデモ目的でのみ利用可能です。完全版では、すべてのポートフォリオ企業に包括的な情報を含む専用ページが用意されます。' },
  },
}
